/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { EvidencePart, ProgramData, ProgramSkill } from "@interfaces/Interfaces";
import { Text, View } from "@react-pdf/renderer";
import { getDisplayTitleForPDFExport } from "@utils/stringUtils";
import { updatePopoverTriggerWidth } from "@utils/popoverUtils";
import * as EvidenceUtils from "@utils/evidenceUtils";
import { IonRadio, IonItem, IonRadioGroup, IonSelect, IonList, IonSelectOption } from "@ionic/react";
import * as ProgramUtils from "@utils/programUtils";

type Props = {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  definition: EvidencePart;
  onValueChanged?: (value: any) => void;
  data?: any;
  isDisplay?: boolean;
  isPDF?: boolean;
  pdfStyles: any;
  initialValue?: any;
  skills?: ProgramSkill[];
  preselectedSkill?: ProgramSkill | null;
  program?: ProgramData | null;
  evidenceJSON?: Record<string, unknown>;
};

const SkillsSelect: React.FC<Props> = (props) => {
  const [value, setValue] = useState<string>(props.initialValue ?? "");

  const allowedSkills = useMemo(() => {
    const values = EvidenceUtils.getListOfValues(props.definition);
    const section = ProgramUtils.isRenfrew(props.program?.ID)
      ? EvidenceUtils.getRenfrewSkillSectionForSelect(props.definition, props.evidenceJSON)
      : undefined;
    const skills = props.skills ?? [];
    const namedSkills = values.length ? skills.filter((skill) => values.includes(skill.Name.trim())) : skills;

    if (section) {
      return namedSkills.filter((skill) => skill.Section === section);
    } else {
      return namedSkills;
    }
  }, [props.skills, props.definition, props.program?.ID, props.evidenceJSON]);

  const valueChanged = (event: any): void => {
    const _value = event.detail.value;

    setValue(_value);
    props.onValueChanged && props.onValueChanged(_value);
  };

  useEffect(() => {
    if (props.preselectedSkill) {
      setValue(props.preselectedSkill.Name);
      props.onValueChanged && props.onValueChanged(props.preselectedSkill.Name);
    }
  }, [props.preselectedSkill?.ID]);

  if (props.isPDF) {
    return (
      <View>
        <Text style={props.pdfStyles.reflectionHeader}>{getDisplayTitleForPDFExport(props.definition)}</Text>
        <Text style={props.pdfStyles.evidenceDate}>{props.data}</Text>
      </View>
    );
  }

  if (props.isDisplay) {
    return (
      <div className="readEvidenceBlock">
        <div className="readEvidenceHeader">{props.definition["Display Title"] ?? props.definition.Name}</div>
        <div className="readEvidenceText">{props.data}</div>
        <div className="h-[1px] w-full bg-grey-30 mt-4" />
      </div>
    );
  }

  return (
    <div className="reflectionActivityTypeContainer">
      <IonList
        mode="md"
        className="reflectionActivityList w-full"
        color="white"
      >
        <IonItem
          mode="md"
          className="reflectionActivityItem"
          color="white"
        >
          <IonSelect
            color="white"
            mode="md"
            className="reflectionActivitySelectItem w-full"
            interface="popover"
            justify="space-between"
            interfaceOptions={{
              size: "cover",
              alignment: "start",
              cssClass: "evidence-select-popover",
            }}
            placeholder={props.definition["Placeholder Text"] || "Select skill"}
            value={value ?? undefined}
            onIonChange={valueChanged}
            onClick={updatePopoverTriggerWidth}
          >
            {allowedSkills
              .sort((a, b) => a.Name.localeCompare(b.Name))
              .map((item) => {
                return (
                  <IonSelectOption
                    color="white"
                    key={item.ID}
                    value={item.Name}
                  >
                    {item.Name}
                  </IonSelectOption>
                );
              })}
          </IonSelect>
        </IonItem>
      </IonList>
    </div>
  );
};

export default SkillsSelect;
